/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\ServerBasicInfoBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the telephonyServers module.
----------------------------------------------------------------*/


import { Server } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Toggle } from '../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormData = Record<string, any>;

interface Props {
    formData: FormData;
    errors: Record<string, string>;
    onInputChange: (field: string, value: unknown) => void;
}

const INPUT_CLS = 'px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent';

interface FieldDef {
    key: string; ttKey: string; labelKey: string; required?: boolean; type?: string;
    debugKey?: string; placeholder?: string; parse?: (v: string) => unknown;
    min?: string; max?: string;
}

export function ServerBasicInfoBox({ formData, errors, onInputChange }: Props) {
    const { t } = useI18n();
    const { isActive: debug } = useDebugMode();

    const textCls = (field: string) =>
        `${INPUT_CLS} ${errors[field] ? 'border-red-500' : 'border-border'}`;

    const DebugKey = ({ name }: { name: string }) =>
        debug ? <span className="block text-xs text-muted-foreground font-mono">{name}</span> : null;

    const fields: FieldDef[] = [
        { key: 'name',                 ttKey: 'telephonyServers.tooltips.serverName',         labelKey: 'telephonyServers.serverName',        required: true,  debugKey: 'name',                 placeholder: 'telephonyServers.placeholders.serverName' },
        { key: 'description',          ttKey: 'telephonyServers.tooltips.description',         labelKey: 'telephonyServers.description',                        debugKey: 'description',          placeholder: 'telephonyServers.placeholders.description' },
        { key: 'host',                 ttKey: 'telephonyServers.tooltips.host',               labelKey: 'telephonyServers.host',              required: true,  debugKey: 'host',                 placeholder: 'telephonyServers.placeholders.host' },
        { key: 'port',                 ttKey: 'telephonyServers.tooltips.sshPort',            labelKey: 'telephonyServers.sshPort',           required: true,  type: 'number', debugKey: 'port', parse: (v) => parseInt(v) || 22 },
        { key: 'username',             ttKey: 'telephonyServers.tooltips.sshUsername',        labelKey: 'telephonyServers.sshUsername',       required: true,  debugKey: 'username',             placeholder: 'telephonyServers.placeholders.sshUsername' },
        { key: 'password',             ttKey: 'telephonyServers.tooltips.sshPassword',        labelKey: 'telephonyServers.sshPassword',                        type: 'password', debugKey: 'password', placeholder: 'telephonyServers.placeholders.sshPassword' },
    ];

    return (
        <FormBox title={t('telephonyServers.sections.basicInfo')} icon={<Server className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {fields.map(({ key, ttKey, labelKey, required, type = 'text', debugKey, placeholder, parse }) => (
                    <>
                        <div key={`lbl-${key}`} className="space-y-1">
                            <SimpleTooltip content={t(ttKey)} position="top" maxWidth="300px">
                                <label className="text-sm font-medium text-foreground">
                                    {t(labelKey)}{required && ' *'}
                                    {debugKey && <DebugKey name={debugKey} />}
                                </label>
                            </SimpleTooltip>
                        </div>
                        <input key={`inp-${key}`} type={type} value={formData[key] ?? ''} onChange={(e) => onInputChange(key, parse ? parse(e.target.value) : e.target.value)} className={textCls(key)} placeholder={placeholder ? t(placeholder) : undefined} />
                    </>
                ))}

                {/* Monitoring */}
                <div className="space-y-1">
                    <SimpleTooltip content={t('telephonyServers.tooltips.enableMonitoring')} position="top" maxWidth="300px">
                        <label className="text-sm font-medium text-foreground">
                            {t('telephonyServers.enableMonitoring')}
                            {debug && <span className="block text-xs text-muted-foreground font-mono">monitoring_enabled</span>}
                        </label>
                    </SimpleTooltip>
                </div>
                <div className="flex items-center">
                    <Toggle value={formData.monitoringEnabled} onChange={(v) => onInputChange('monitoringEnabled', v)} label="" />
                </div>

                {/* Health Check Interval */}
                <div className="space-y-1">
                    <SimpleTooltip content={t('telephonyServers.tooltips.healthCheckInterval')} position="top" maxWidth="300px">
                        <label className="text-sm font-medium text-foreground">
                            {t('telephonyServers.healthCheckInterval')}
                            {debug && <span className="block text-xs text-muted-foreground font-mono">health_check_interval</span>}
                        </label>
                    </SimpleTooltip>
                </div>
                <input type="number" value={formData.healthCheckInterval} onChange={(e) => onInputChange('healthCheckInterval', parseInt(e.target.value) || 30)} className={`${INPUT_CLS} border-border`} placeholder={t('telephonyServers.placeholders.healthCheckInterval')} min="10" max="3600" />
            </div>
        </FormBox>
    );
}
